import React from 'react'
import styled from 'styled-components'
import HistoryRoundedIcon       from '@mui/icons-material/HistoryRounded'
import BlockRoundedIcon         from '@mui/icons-material/BlockRounded'
import VerifiedRoundedIcon      from '@mui/icons-material/VerifiedRounded'
import RateReviewRoundedIcon    from '@mui/icons-material/RateReviewRounded'
import ScheduleRoundedIcon      from '@mui/icons-material/ScheduleRounded'
import EventBusyRoundedIcon     from '@mui/icons-material/EventBusyRounded'
import { RAG as RAG_LEVELS } from 'features/dashboard/charts/scoreColour'

const KIND_META = {
  skipped:   { Icon: BlockRoundedIcon,      label: 'Skipped',           tint: RAG_LEVELS.none.fg },
  satisfied: { Icon: VerifiedRoundedIcon,   label: 'Satisfied',         tint: RAG_LEVELS.low.fg },
  resolved:  { Icon: VerifiedRoundedIcon,   label: 'Resolved',          tint: RAG_LEVELS.low.fg },
  in_review: { Icon: RateReviewRoundedIcon, label: 'In review',         tint: RAG_LEVELS.medium.fg },
  pending:   { Icon: ScheduleRoundedIcon,   label: 'Awaiting evidence', tint: RAG_LEVELS.none.fg },
  expired:   { Icon: EventBusyRoundedIcon,  label: 'Expired',           tint: RAG_LEVELS.critical.fg },
  overdue:   { Icon: EventBusyRoundedIcon,  label: 'Overdue',           tint: RAG_LEVELS.high.fg },
  changed:   { Icon: HistoryRoundedIcon,    label: 'Changed',           tint: RAG_LEVELS.none.fg },
}

const formatRel = iso => {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diffMin = Math.round((now - then) / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.round(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

/**
 * Compact audit trail surface — used on obligation cards, action rows, and
 * anywhere a record has provenance worth showing. Lists one chip per audit
 * entry: kind icon + actor + relative time + optional note.
 *
 * Props:
 *   entries: [{ kind, at, actorName, note }]
 *   compact?: bool — shows only most recent entry when true
 */
const AuditChip = ({ entries = [], compact = false }) => {
  if (!entries.length) return null
  const visible = compact ? entries.slice(0, 1) : entries

  return (
    <Wrap aria-label='Audit trail'>
      {visible.map((e, i) => {
        const meta = KIND_META[e.kind] || KIND_META.changed
        const Icon = meta.Icon
        return (
          <Entry key={`${e.kind}-${i}`}>
            <IconChip $tint={meta.tint}><Icon style={{ fontSize: 12 }} /></IconChip>
            <Text>
              <strong>{meta.label}</strong>
              {e.actorName && <> by {e.actorName}</>}
              {e.at && <Time>· {formatRel(e.at)}</Time>}
              {e.note && <Note>“{e.note}”</Note>}
            </Text>
          </Entry>
        )
      })}
    </Wrap>
  )
}

export default AuditChip

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  margin-top: 6px;
`

const Entry = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const IconChip = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px;
  border-radius: 50%;
  background: ${({ $tint }) => `${$tint}1A`};
  color: ${({ $tint }) => $tint};
  flex-shrink: 0;
`

const Text = styled.span`
  display: inline-flex; align-items: center; flex-wrap: wrap; gap: 4px;
  strong { color: ${({ theme }) => theme.colors.haiti}; font-weight: 700; }
`

const Time = styled.span``

const Note = styled.span`
  font-style: italic;
  color: ${({ theme }) => theme.colors.grey[800]};
`
